import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { normalizePublicGameState } from "@fiveout/core";
import type { BonusStackChoice, DeckConfig, HouseRules, PlayerMove, PublicGameState, SwapChoice, TeamId } from "@fiveout/core";
import type { Crest } from "../theme/crests";
import type { BillingStatus } from "../theme/entitlements";
import type { ProductId } from "../billing/catalog";
import type { OpenMatchRecord } from "./openMatches";
import { auth, authReady, db } from "./firebase";
import { recordNetDiag } from "./netDiag";

// In dev, Vite proxies /api to the local Go server (see vite.config.ts + scripts/dev.sh).
// In prod, VITE_API_BASE_URL points at the deployed Cloud Run service URL.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

/** Exported for voiceApi.ts, whose routes share the token handling and the error shape. */
export async function authedFetch(path: string, init?: RequestInit, playerId?: string) {
  // Every one of these endpoints verifies an ID token, so this has to wait for
  // auth rather than just reading auth.currentUser, which is null until the
  // session finishes restoring — without the wait, the first request after any
  // page load went out with no Authorization header and came back 401.
  //
  // Live user first, and only fall back to waiting: authReady() resolves once
  // and keeps that user, so a host who signs in with Google after an anonymous
  // session would otherwise keep presenting the anonymous token and be told
  // they aren't allowed to host.
  const user = auth.currentUser ?? (await authReady());
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(playerId ? { "X-Player-Id": playerId } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    // The server answers a refusal with `{"error": "..."}`, and that sentence is
    // written to be read by a player — "a round needs at least 2 players" is the
    // whole of what they need to know. Screens show `e.message` directly, so
    // wrapping it in a status code and a JSON envelope put both on screen.
    // Anything that isn't that shape falls back to the raw body, which is the
    // only useful thing to say about an error nobody wrote for a human.
    let message = body;
    try {
      const parsed: unknown = JSON.parse(body);
      if (parsed && typeof parsed === "object" && typeof (parsed as { error?: unknown }).error === "string") {
        message = (parsed as { error: string }).error;
      }
    } catch {
      /* not JSON; the body is as good as it gets */
    }
    throw new Error(message || `request failed (${res.status})`);
  }
  return res.json();
}

export interface CreateGameResponse {
  gameId: string;
  code: string;
  /** The host is automatically seated as the first player using hostNickname. */
  playerId: string;
}

export interface HostStatus {
  googleAuthenticated: boolean;
  /** This account's email is on the server's `allowedHosts` list. */
  allowedHost: boolean;
  /** Only from a server with billing on, for a Google account. */
  billing?: BillingStatus;
}

/**
 * What this account is allowed to do — `GET /host/status`.
 *
 * `allowedHosts` is closed to clients in the security rules (it is keyed by
 * email, so a client read rule would be a query surface over other people's
 * addresses), which is why this is an API call and not a Firestore read.
 *
 * Returns null on ANY failure — offline, server down, not signed in — and
 * callers must treat null as "don't know", never as "no". The one caller today
 * comps premium decks to approved hosts, and a flat network blip must not take
 * somebody's card deck away mid-session.
 */
export async function fetchHostStatus(): Promise<HostStatus | null> {
  try {
    const body = await authedFetch("/host/status", { method: "GET" });
    if (typeof body?.allowedHost !== "boolean") return null;
    return { googleAuthenticated: !!body.googleAuthenticated, allowedHost: body.allowedHost, billing: body.billing };
  } catch {
    return null;
  }
}

/**
 * Finishes a simulated purchase and answers with what the account now holds. Dev
 * and local only: the route does not exist on a production server. This is where
 * a real purchase will hand over to the store instead.
 */
export function purchaseProduct(product: ProductId): Promise<BillingStatus> {
  return authedFetch("/billing/purchase", { method: "POST", body: JSON.stringify({ product }) });
}

/** Undoes every simulated purchase on this account, so a tester can start over. Dev and local only. */
export function resetPurchases(): Promise<BillingStatus> {
  return authedFetch("/billing/reset", { method: "POST" });
}

/** Host-only: deletes a table for everyone at it, freeing the host's place to host another. */
export async function closeTable(gameId: string, hostPlayerId: string): Promise<void> {
  await authedFetch(`/games/${gameId}/close`, { method: "POST" }, hostPlayerId);
}

/**
 * The open games this ACCOUNT has sat at, on any device — `GET /me/games`, as
 * Open Matches records. The server reads them off the private game documents no
 * client can query, keyed by the verified uid, so this only ever answers for the
 * caller. Null on any failure: the list on this browser still stands.
 */
export async function fetchAccountMatches(): Promise<OpenMatchRecord[] | null> {
  try {
    const body = await authedFetch("/me/games", { method: "GET" });
    if (!Array.isArray(body?.games)) return null;
    return body.games
      .filter((g: unknown): g is { gameId: string; code: string; nickname: string; lastActivityAtMs?: number } => {
        const r = g as Record<string, unknown>;
        return typeof r?.gameId === "string" && typeof r.code === "string" && typeof r.nickname === "string";
      })
      .map((g: { gameId: string; code: string; nickname: string; lastActivityAtMs?: number }) => ({
        gameId: g.gameId,
        code: g.code,
        nickname: g.nickname,
        seenAtMs: typeof g.lastActivityAtMs === "number" ? g.lastActivityAtMs : 0,
      }));
  } catch {
    return null;
  }
}

/**
 * Where this device thinks it is, for the hosting stats.
 *
 * The server records match times in UTC, which answers "how many matches this
 * week" but not "which evenings do people play" — pooling hosts in different
 * zones under one clock smears exactly that peak. Only the client knows the
 * answer, so it says so at match creation.
 *
 * Read here rather than passed in by the caller: it's a property of the device,
 * not a choice anyone is making on the host screen.
 *
 * Best-effort by design. If `Intl` throws or reports nothing usable, the match is
 * simply recorded in UTC only — never a reason to fail creating a game.
 */
function hostClock(): { hostTimeZone?: string; hostUtcOffsetMinutes?: number } {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!zone) return {};
    // getTimezoneOffset() is minutes *west* of UTC — the opposite sign to every
    // other representation, including the one the server stores. Negate it.
    return { hostTimeZone: zone, hostUtcOffsetMinutes: -new Date().getTimezoneOffset() };
  } catch {
    return {};
  }
}

/**
 * An online table's turn timer. Every online turn is timed whether the host
 * picks a length or not - the server auto-plays a player who walks away after
 * `defaultDisconnectGraceSeconds` (60) - so the host chooses how long, never
 * whether. Mirrors `minTimerSeconds` / `maxTimerSeconds` in the Go manager.
 */
export const ONLINE_TURN_TIMER = { min: 5, max: 60, step: 5 } as const;

/** Host-only: creates a new game and seats the host as the first player. Requires the host to already be Google-OAuth-signed-in and allow-listed server-side. */
export async function createGame(config: {
  turnTimerSeconds: number | null;
  hostNickname: string;
  deckConfig?: DeckConfig;
  houseRules?: HouseRules;
  /** The host's currently-selected deck and table, so every joiner renders them even if unlocked only for the host. */
  hostThemeId?: string;
  hostVenueId?: string;
  /**
   * The host's level crest, so every client can draw it on their deck. The
   * server checks it against the cloud save of a Google sign-in, which a host
   * always is; see `joinGame`.
   */
  crest?: Crest;
}): Promise<CreateGameResponse> {
  return authedFetch("/games", { method: "POST", body: JSON.stringify({ ...config, ...hostClock() }) });
}

/**
 * Host-only: changes the table settings from the lobby, between rounds. The
 * server rejects this once a round is in progress, and rebroadcasts the config
 * in the public view so joiners always see what they're about to play.
 *
 * Every field is optional and only what is supplied is written, so the deck
 * panel and the cosmetics can each send their own change without having to
 * restate the other's.
 */
export async function updateGameConfig(
  gameId: string,
  playerId: string,
  config: {
    deckConfig?: DeckConfig;
    houseRules?: HouseRules;
    turnTimerSeconds?: number | null;
    /** The deck and table everyone at this table plays with — see `createGame`. */
    hostThemeId?: string;
    hostVenueId?: string;
    /**
     * Wipe the leaderboard along with this change.
     *
     * Rides on the config update rather than being its own call, for the reason
     * `ban` rides on a removal: the host is answering ONE question asked at the
     * moment they changed the table, and a clear sent separately could land
     * without the change that prompted it.
     */
    clearMatch?: boolean;
  },
): Promise<void> {
  await authedFetch(
    `/games/${gameId}/config`,
    {
      method: "POST",
      body: JSON.stringify({
        deckConfig: config.deckConfig,
        houseRules: config.houseRules,
        // `clearTimer` distinguishes "turn timer off" from "field not supplied".
        turnTimerSeconds: config.turnTimerSeconds ?? undefined,
        clearTimer: config.turnTimerSeconds === null,
        hostThemeId: config.hostThemeId,
        hostVenueId: config.hostVenueId,
        clearMatch: config.clearMatch,
      }),
    },
    playerId,
  );
}

export interface JoinGameResponse {
  gameId: string;
  playerId: string;
}

/**
 * Anyone (host included, after creating) joins by 4-digit code + a 3-10 character nickname. Non-host players must be signed in anonymously first.
 *
 * `crest` is the player's level crest, published so every client draws it on
 * their deck. A claim, not a fact: for a Google sign-in the server rebuilds it
 * from the cloud save, for a guest it only checks the shape. Optional, and a
 * crest the server cannot use costs the crest, never the seat.
 */
/**
 * `watch` joins as an observer rather than taking a seat ("Just watch"). A
 * nickname that re-takes an existing entry goes back to wherever that entry was.
 */
export async function joinGame(code: string, nickname: string, crest?: Crest, watch = false): Promise<JoinGameResponse> {
  return authedFetch("/games/join", { method: "POST", body: JSON.stringify({ code, nickname, crest, watch }) });
}

/**
 * One look at a game's public state, for a screen that is not in the game (the
 * Open Matches list). Null means the document is not there, i.e. the match has
 * been cleaned up; a network failure THROWS, because "I could not ask" and "it
 * is gone" must not be the same answer - the first keeps a remembered match, the
 * second forgets it. Offline, `getDoc` rejects rather than reporting a missing
 * document from an empty cache, which is what makes that distinction safe here
 * (the snapshot path above has to guard against the opposite).
 */
export async function fetchPublicGame(gameId: string): Promise<PublicGameState | null> {
  await authReady();
  const snap = await getDoc(doc(db, "games", gameId));
  return snap.exists() ? normalizePublicGameState(snap.data() as PublicGameState) : null;
}

/**
 * Whether a remembered match can still be rejoined: `GET /games/{id}/open`,
 * which checks the join code still points at this game and that the game has
 * not expired. Asked alongside `fetchPublicGame` because that document cannot
 * answer it - an ended match's stays behind as `game-over`. A closed match is
 * an ordinary `false`; a failure THROWS, so "could not ask" is never mistaken
 * for "over".
 */
export async function isMatchOpen(gameId: string, code: string): Promise<boolean> {
  const body = await authedFetch(`/games/${encodeURIComponent(gameId)}/open?code=${encodeURIComponent(code)}`, { method: "GET" });
  return body?.open === true;
}

/**
 * Re-dresses the caller in the crest they wear now - after each round, and when
 * the Account screen changes it. Without this a player keeps the crest they
 * joined in for the whole match, however far they level.
 *
 * `gameId` is the lobby's id (what joinGame returned), never a party table's;
 * the server finds the table from it. Whose crest changes is the signed-in
 * token's, so there is no player id to send. Checked exactly as on join, and
 * an unchanged crest is a no-op on the server. Rate limited (12 a minute per
 * player), so call it on those events rather than on every render. Rejects on
 * any failure - callers decide whether a missed refresh is worth mentioning.
 */
export async function updateCrest(gameId: string, crest: Crest): Promise<void> {
  await authedFetch(`/games/${gameId}/crest`, { method: "POST", body: JSON.stringify({ crest }) });
}

/**
 * Deals a round. Also the "next round" path: after a round ends the game sits in
 * round-over/game-over, and dealing from there banks the finished round into the
 * match totals and re-deals with the same players.
 *
 * Callers must surface the rejection. This used to be fired and forgotten from
 * the lobby, which is why "Deal the Cards" looked like a dead button after a
 * round instead of showing why it failed.
 */
export async function startGame(gameId: string, playerId: string): Promise<void> {
  await authedFetch(`/games/${gameId}/start`, { method: "POST" }, playerId);
}

/**
 * Host-only: re-orders the party roster before a deal (party lobbies only —
 * the server rejects it on a table of 8 or fewer, and on a child document).
 *
 * There is no "shuffle the tables" call because there is no such thing: the
 * groups are contiguous slices of the roster order, so permuting that order IS
 * the re-draw. The new order arrives on the next snapshot and every client
 * re-derives the same split from it.
 */
export async function shufflePlayers(gameId: string, playerId: string): Promise<void> {
  await authedFetch(`/games/${gameId}/shuffle`, { method: "POST" }, playerId);
}

/**
 * Host-only: two players trade places in the party roster, which trades their
 * tables at the next deal — the hand-placed counterpart of `shufflePlayers`,
 * with the same rules (party lobbies, parent only, before a deal or between
 * rounds).
 *
 * Either may be on the waiting list. The server then seats everyone waiting
 * first, level with last place exactly as the deal would, because a waiting
 * player's table is their place at the END of the roster and there is no other
 * way to put them anywhere else.
 */
export async function swapPlayers(gameId: string, playerId: string, a: string, b: string): Promise<void> {
  await authedFetch(`/games/${gameId}/swap`, { method: "POST", body: JSON.stringify({ a, b }) }, playerId);
}

/** One edit to the host's table layout (`arrangeTables`). */
export type TableOp =
  | { op: "add" }
  | { op: "remove"; table: number }
  | { op: "move"; playerId: string; table: number }
  | { op: "rank" };

/**
 * Host-only: lays out the lobby's tables by hand (`POST /games/{id}/tables`) -
 * add a table (up to 5), remove one (its players are re-seated at the smallest
 * remaining), move a player to any table, or re-fill the tables by match
 * standings. Allowed where swapping is: before a deal and between rounds, on
 * the parent only. The first edit turns the auto split into a custom layout;
 * the new layout arrives on the next snapshot, as a swap's does.
 */
export async function arrangeTables(gameId: string, playerId: string, op: TableOp): Promise<void> {
  await authedFetch(`/games/${gameId}/tables`, { method: "POST", body: JSON.stringify(op) }, playerId);
}

/** One edit to the lobby's teams (`arrangeTeams`). */
export type TeamOp =
  | { op: "mode"; mode: "teams" | "ffa" }
  | { op: "add" }
  | { op: "remove"; team: TeamId }
  | { op: "move"; playerId: string; team: TeamId };

/**
 * Host-only: edits the lobby's teams (`POST /games/{id}/teams`) - switch between
 * Teams and free-for-all, bring in the next color, remove a team (its players go
 * to the smallest remaining), or put a player on a team. Allowed where
 * `arrangeTables` is: before a deal and between rounds. The result arrives on the
 * next snapshot, as a table edit's does.
 */
export async function arrangeTeams(gameId: string, playerId: string, op: TeamOp): Promise<void> {
  await authedFetch(`/games/${gameId}/teams`, { method: "POST", body: JSON.stringify(op) }, playerId);
}

/**
 * Host-only: shows the MATCH podium to everyone (`POST /games/{id}/podium`).
 * Addressed to the LOBBY (a party's parent). The server stamps `matchPodiumAtMs`
 * on the lobby document and every client plays the podium when it sees a new
 * value; refused until a round has finished.
 */
export async function showMatchPodium(gameId: string, playerId: string): Promise<void> {
  await authedFetch(`/games/${gameId}/podium`, { method: "POST", body: "{}" }, playerId);
}

/**
 * Raises or lowers this player's own hand in a party. Addressed to the LOBBY
 * (the parent), whose roster is what every table's screen reads; the server
 * takes who is asking from the token, so there is no player argument to get
 * wrong. A hand stays up until its owner lowers it.
 */
export async function setHandRaised(lobbyGameId: string, playerId: string, raised: boolean): Promise<void> {
  await authedFetch(`/games/${lobbyGameId}/hand`, { method: "POST", body: JSON.stringify({ raised }) }, playerId);
}

/**
 * Switches this player between watching and playing. Anyone may, the host
 * included; who is the signed-in token, never `playerId`. A seat can only be
 * given up between rounds; taking one back mid-match waits for the next deal.
 */
export async function setObserving(lobbyGameId: string, playerId: string, observing: boolean): Promise<void> {
  await authedFetch(`/games/${lobbyGameId}/observe`, { method: "POST", body: JSON.stringify({ observing }) }, playerId);
}

/**
 * Tells the party which table this player is looking at while their own is
 * still playing — `""` for "back at my own table". It is what puts "Away" on
 * their deck at home, so their table knows why nobody is answering.
 */
export async function setViewing(lobbyGameId: string, playerId: string, tableGameId: string): Promise<void> {
  await authedFetch(
    `/games/${lobbyGameId}/viewing`,
    { method: "POST", body: JSON.stringify({ gameId: tableGameId }) },
    playerId,
  );
}

/**
 * Host-only: removes somebody from the game — in the lobby, between rounds, or
 * mid-round.
 *
 * `gameId` is the LOBBY's document, which in a party means the parent and never
 * the table the player is sitting at. The host's own seat is at one table and
 * the player they are removing may be at another, so an instance is not
 * somewhere the server can recognise the caller as the host at all; the parent
 * knows the whole roster and which table holds them, and moves both in one
 * transaction.
 *
 * `playerId` is the second argument the server needs and `hostPlayerId` the
 * third, which is easy to transpose — they are both opaque ids. The parameter
 * order matches the sentence: remove THIS player, on the authority of THIS host.
 *
 * `ban` also blocks them from coming back on this game's code. It rides on the
 * removal rather than being its own call because the host is answering one
 * question — "are they coming back?" — and a ban sent separately could land
 * without the removal that justified it.
 */
export async function kickPlayer(
  gameId: string,
  playerId: string,
  hostPlayerId: string,
  ban = false,
): Promise<void> {
  await authedFetch(
    `/games/${gameId}/kick`,
    { method: "POST", body: JSON.stringify({ playerId, ban }) },
    hostPlayerId,
  );
}

/**
 * Host-only: lets a blocked player back onto the join code. It does NOT re-seat
 * them — they join with the code like anyone else.
 */
export async function unbanPlayer(gameId: string, playerId: string, hostPlayerId: string): Promise<void> {
  await authedFetch(
    `/games/${gameId}/unban`,
    { method: "POST", body: JSON.stringify({ playerId }) },
    hostPlayerId,
  );
}

export async function submitMove(gameId: string, playerId: string, move: PlayerMove): Promise<void> {
  await authedFetch(`/games/${gameId}/move`, { method: "POST", body: JSON.stringify(move) }, playerId);
}

/**
 * Spends the acting player's streak (Jourdan's Streaks).
 *
 * The target is the only thing the caller ever chooses, and under Blue Shell not
 * even that — the server picks the leaders, so `targetPlayerId` is omitted and a
 * client that sent one would be ignored. How many piles a mark is worth and
 * which piles shut stay the rule's either way, so there is nothing here for a
 * client to get wrong or to lie about beyond naming somebody at its own table,
 * which the server checks.
 */
export async function submitStreak(
  gameId: string,
  playerId: string,
  targetPlayerId: string | null,
): Promise<void> {
  await authedFetch(
    `/games/${gameId}/streak`,
    { method: "POST", body: JSON.stringify(targetPlayerId ? { targetPlayerId } : {}) },
    playerId,
  );
}

export async function submitBonusChoice(gameId: string, playerId: string, choice: BonusStackChoice): Promise<void> {
  await authedFetch(`/games/${gameId}/bonus-choice`, { method: "POST", body: JSON.stringify(choice) }, playerId);
}

/** One owed Swap. The body is the choice itself: `{"stackIndices":[a,b]}`. */
export async function submitSwapChoice(gameId: string, playerId: string, choice: SwapChoice): Promise<void> {
  await authedFetch(`/games/${gameId}/swap-choice`, { method: "POST", body: JSON.stringify(choice) }, playerId);
}

export async function leaveOrDisconnect(gameId: string, playerId: string): Promise<void> {
  await authedFetch(`/games/${gameId}/leave`, { method: "POST" }, playerId);
}

export async function reportTimeout(gameId: string): Promise<void> {
  await authedFetch(`/games/${gameId}/timeout`, { method: "POST" });
}

/**
 * Subscribes to a game's public state via Firestore's real-time listeners
 * (the design doc's chosen mechanism, instead of raw WebSockets). The Go
 * server is the only writer of this document.
 *
 * Two things this has to get right, both of which it previously didn't:
 *
 *  - Attach only once auth is up. The security rules require `request.auth`,
 *    so attaching on a cold load raced session restoration and got
 *    permission-denied.
 *  - Treat an errored listener as dead. Firestore does NOT re-attach a listener
 *    after it errors, and this had no error callback at all, so the failure was
 *    swallowed and the UI waited forever for a snapshot that could never come.
 *    Now it reports and re-attaches with backoff.
 */
export function subscribeToGame(
  gameId: string,
  onUpdate: (state: PublicGameState) => void,
  onError?: (err: Error) => void,
  /** The game document isn't there — ended, expired, or cleaned up. Not an error, and not retryable. */
  onMissing?: () => void,
): () => void {
  let cancelled = false;
  let unsubscribeSnapshot: (() => void) | null = null;
  let retryTimer: number | undefined;
  let attempt = 0;

  const scheduleRetry = () => {
    if (cancelled) return;
    // 0.5s, 1s, 2s … capped at 15s, so a long outage keeps retrying quietly.
    const delay = Math.min(15000, 500 * 2 ** attempt++);
    retryTimer = window.setTimeout(attach, delay);
  };

  const fail = (err: unknown) => {
    if (cancelled) return;
    recordNetDiag({ kind: "ERR", detail: err instanceof Error ? err.message : String(err) });
    onError?.(err instanceof Error ? err : new Error(String(err)));
    scheduleRetry();
  };

  function attach() {
    if (cancelled) return;
    authReady().then(() => {
      if (cancelled) return;
      unsubscribeSnapshot = onSnapshot(
        doc(db, "games", gameId),
        (snap) => {
          attempt = 0; // a good snapshot resets the backoff
          if (snap.exists()) {
            // Traced BEFORE normalization and before the store sees it, so the
            // timestamp is arrival at the client and nothing downstream can
            // shift it. See netDiag.ts for what the trace answers.
            const raw = snap.data() as PublicGameState;
            recordNetDiag({
              kind: "SNAP",
              version: raw.version,
              seq: raw.eventSeq ?? 0,
              events: raw.lastEvents?.length ?? 0,
              fromCache: snap.metadata.fromCache,
            });
            // Normalize before anything downstream sees it: this is the single point
            // where server-produced data enters the app, and Go's nil slices/maps arrive
            // as `null` where the TypeScript types promise an array or an object.
            onUpdate(normalizePublicGameState(raw));
            return;
          }
          // A game that isn't there delivers a perfectly successful snapshot that
          // simply doesn't exist. Dropping it silently is what left players on
          // "Connecting…" indefinitely with a Retry button that could only ever
          // re-subscribe to the same absent document.
          //
          // Only believe it from the server, though: offline, the SDK answers
          // from cache, and a doc it has never seen also reads as "doesn't
          // exist" — treating that as authoritative would throw players out of a
          // live game the moment their signal dropped.
          if (!snap.metadata.fromCache) onMissing?.();
        },
        (err) => {
          unsubscribeSnapshot = null; // this listener is spent
          fail(err);
        },
      );
    }, fail);
  }

  attach();

  return () => {
    cancelled = true;
    if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    unsubscribeSnapshot?.();
  };
}
